import { i18n } from 'boot/i18n';
export interface IImageData {
  ratio: number;
  width: number;
  height: number;
  simplifiedFormatted: string;
}
const t = i18n.global.t;

/**
 * Function to calculate relative luminance
 * @remarks Calculates the relative luminance of the color using the WCAG 2.0 formula
 *
 * @param r - red
 * @param g - green
 * @param b - blue
 * @returns the Luminance value
 *
 */
const getLuminance = (r, g, b) => {
  const [rs, gs, bs] = [r, g, b].map((c) => {
    c = c / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
};

/**
 * Function to determine if a color is light or dark
 * @remarks
 *
 * @param color - a string value for the colour, either hex rgb or a named colour
 * @returns true if light or false if dark
 *
 */
const isLightColor = (color: string) => {
  // Handle hex colors
  if (color.startsWith('#')) {
    const hex = color.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    return getLuminance(r, g, b) > 0.5;
  }

  // Handle rgb/rgba colors
  if (color.startsWith('rgb')) {
    const [r, g, b] = color.match(/\d+/g).map(Number);
    return getLuminance(r, g, b) > 0.5;
  }

  // For named colors, create a temporary element to get RGB values
  const elem = document.createElement('div');
  elem.style.color = color;
  document.body.appendChild(elem);
  const rgbColor = window.getComputedStyle(elem).color;
  document.body.removeChild(elem);
  const [r, g, b] = rgbColor.match(/\d+/g).map(Number);
  return getLuminance(r, g, b) > 0.5;
};

/*
 * Taken from https://gist.github.com/6174/6062387
 * Further info: http://stackoverflow.com/questions/105034/how-to-create-a-guid-uuid-in-javascript
 */
function generateRandomId(): string {
  return (
    Math.random().toString(36).substring(2, 15) +
    Math.random().toString(36).substring(2, 15)
  ).toUpperCase();
}

function getNumberOfAdultsText(numberOfAdults: number): string {
  if (numberOfAdults > 0) {
    if (numberOfAdults == 1) {
      return numberOfAdults + ' ' + t('Adult');
    } else if (numberOfAdults > 1) {
      return numberOfAdults + ' ' + t('Adults');
    }
  }
  return null;
}

function getNumberOfChildrenText(numberOfChildren: number): string {
  if (numberOfChildren > 0) {
    if (numberOfChildren == 1) {
      return numberOfChildren + ' ' + t('Child');
    } else if (numberOfChildren > 1) {
      return numberOfChildren + ' ' + t('Children');
    }
  }
  return null;
}

function getNumberOfInfantsText(numberOfInfants: number): string {
  if (numberOfInfants > 0) {
    if (numberOfInfants == 1) {
      return numberOfInfants + ' ' + t('Infant');
    } else if (numberOfInfants > 1) {
      return numberOfInfants + ' ' + t('Infants');
    }
  }
  return null;
}

function getGuestSummaryText(
  numberOfAdults: number,
  numberOfChildren: number,
  numberOfInfants: number,
): string {
  const strs = [];
  const a = getNumberOfAdultsText(numberOfAdults);
  if (a) {
    strs.push(a);
  }
  const c = getNumberOfChildrenText(numberOfChildren);
  if (c) {
    strs.push(c);
  }
  const i = getNumberOfInfantsText(numberOfInfants);
  if (i) {
    strs.push(i);
  }

  return strs.join(', ');
}

function getImageDimensions(url: string): Promise<IImageData> {
  const promise = new Promise<IImageData>((resolve, reject) => {
    if (!url) {
      reject();
    } else {
      const img = new Image();

      img.onload = () => {
        const width = img.naturalWidth;
        const height = img.naturalHeight;

        // Calculate aspect ratio
        const aspectRatio = width / height;

        // Find the greatest common divisor (GCD)
        const gcd = (a, b) => (b === 0 ? a : gcd(b, a % b));
        const divisor = gcd(width, height);

        resolve({
          ratio: aspectRatio,
          width: width,
          height: height,
          simplifiedFormatted: `${width / divisor}:${height / divisor}`,
        } as IImageData);
      };
      img.onerror = reject;
      img.src = url;
    }
  });

  return promise;
}

function checkIfOccupancyIsValid(
  numberOfAdults: number,
  numberOfChildren: number,
  numberOfInfants: number,
  maximumOccupancy: number | null,
  minimumOccupancy: number | null,
  adultMaximumOccupancy: number | null,
  childMaximumOccupancy: number | null,
): boolean {
  let roomTypeOccupancyIsValid = true;

  const numberOfGuests = numberOfAdults + numberOfChildren + numberOfInfants;
  const numberOfChildrenAndInfants = numberOfChildren + numberOfInfants;

  if (
    (maximumOccupancy != null && numberOfGuests > maximumOccupancy) ||
    (adultMaximumOccupancy != null && numberOfAdults > adultMaximumOccupancy) ||
    (childMaximumOccupancy != null &&
      numberOfChildrenAndInfants > childMaximumOccupancy) ||
    (minimumOccupancy != null && numberOfGuests < minimumOccupancy)
  ) {
    roomTypeOccupancyIsValid = false;
  }

  return roomTypeOccupancyIsValid;
}

function getLargestMinimumValue(
  primary: number | null,
  secondary: number | null,
): number | null {
  let value = null;

  if (primary != null && secondary != null) {
    value = Math.max(primary, secondary);
  } else if (primary != null && secondary == null) {
    value = primary;
  } else if (primary == null && secondary != null) {
    value = secondary;
  }

  return value;
}

function getSmallestMaximumValue(
  primary: number | null,
  secondary: number | null,
): number | null {
  let value = null;

  if (primary != null && secondary != null) {
    value = Math.min(primary, secondary);
  } else if (primary != null && secondary == null) {
    value = primary;
  } else if (primary == null && secondary != null) {
    value = secondary;
  }

  return value;
}

export default function useUtils() {
  return {
    isLightColor: isLightColor,
    getGuestSummaryText: getGuestSummaryText,
    getNumberOfAdultsText: getNumberOfAdultsText,
    getNumberOfChildrenText: getNumberOfChildrenText,
    getNumberOfInfantsText: getNumberOfInfantsText,
    generateRandomId: generateRandomId,
    getImageDimensions: getImageDimensions,
    checkIfOccupancyIsValid: checkIfOccupancyIsValid,
    getSmallestMaximumValue: getSmallestMaximumValue,
    getLargestMinimumValue: getLargestMinimumValue,
  };
}
